import { useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "../../../../../components/button/Button"
import { Div } from "../../../../../components/div/Div"
import { Flex } from "../../../../../components/flex/Flex"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { WEB_LINKS } from "../../../../../constants/links"
import { Goodwe, SolaxActive, VictronLogo } from "../../../../../images/xd/icons"
import { theme } from "../../../../../styles/stitches.config"
import { PageContainer } from "../../containers"
import {
  SDescription,
  SIcon,
  SLargeTitle,
  SMainContent,
  SManufacturerCard,
  SManufacturerCardDescription,
  SManufacturerCardFooter,
  SManufacturerCardHeader,
  STextWrapper,
  STitle,
  SWhatNowContent,
  SWhatNowDescription,
  SWhatNowGrid,
  SWhatNowIcon,
  SWhatNowIndex,
  SWhatNowItem,
  SWhatNowTitle,
} from "./AcquisitionThankYou.styled"
import { IconContract } from "./icons/IconContract"
import { IconEnergy } from "./icons/IconEnergy"
import { IconHandshake } from "./icons/IconHandshake"
import { IconInfo } from "./icons/IconInfo"
import { IconLaptop } from "./icons/IconLaptop"
import { IconPhone } from "./icons/IconPhone"
import { IconThumbsDown } from "./icons/IconThumbsDown"
import { IconThumbsUp } from "./icons/IconThumbsUp"

function useWhatNowSteps() {
  const { t } = useTranslation(["acquisitionForm"])

  return useMemo(
    () => [
      {
        index: "01",
        title: t("acquisitionForm:what_now_info_title"),
        description: t("acquisitionForm:what_now_info_description"),
        Icon: IconInfo,
      },
      {
        index: "02",
        title: t("acquisitionForm:what_now_contract_title"),
        description: t("acquisitionForm:what_now_contract_description"),
        Icon: IconContract,
      },
      {
        index: "03",
        title: t("acquisitionForm:what_now_sign_title"),
        description: t("acquisitionForm:what_now_sign_description"),
        Icon: IconHandshake,
      },
      {
        index: "04",
        title: t("acquisitionForm:what_now_termination_title"),

        description: t("acquisitionForm:what_now_termination_description"),
        Icon: IconPhone,
      },
      {
        index: "05",
        title: t("acquisitionForm:what_now_reject_title"),
        description: t("acquisitionForm:what_now_reject_description"),
        Icon: IconThumbsDown,
      },
      {
        index: "06",
        title: t("acquisitionForm:what_now_accept_title"),
        description: t("acquisitionForm:what_now_accept_description"),
        Icon: IconThumbsUp,
      },
      {
        index: "07",
        title: t("acquisitionForm:what_now_register_title"),
        description: t("acquisitionForm:what_now_register_description"),
        Icon: IconLaptop,
      },
      {
        index: "08",
        title: t("acquisitionForm:what_now_welcome_title"),
        description: t("acquisitionForm:what_now_welcome_description"),
        Icon: IconEnergy,
      },
    ],
    [t]
  )
}

export const AcquisitionThankYou = () => {
  const { t } = useTranslation(["acquisitionForm"])
  const whatNowSteps = useWhatNowSteps()

  const ref = useRef<HTMLElement | null>(null)

  function handleClick() {
    ref.current?.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <PageContainer>
      <SMainContent>
        <Div>
          <SIcon />
        </Div>

        <STextWrapper>
          <STitle>{t("acquisitionForm:thank_you")}</STitle>
          <Spacer size={theme.space.s8} />
          <SDescription>{t("acquisitionForm:thank_you_description_1")}</SDescription>
          <Spacer size={theme.space.s6} />
          <SDescription>{t("acquisitionForm:thank_you_description_2")}</SDescription>
          <Spacer size={theme.space.s8} />
          <Button onClick={handleClick} isFullwidth css={{ borderRadius: theme.sizes.s2 }}>
            {t("acquisitionForm:i_want_delta_link")}
          </Button>
        </STextWrapper>
      </SMainContent>

      <section style={{ padding: "20px" }}>
        <SLargeTitle>{t("acquisitionForm:what_is_next")}</SLargeTitle>
        <Spacer size={theme.space.s10} />
        <SWhatNowGrid>
          {whatNowSteps.map(({ Icon, ...step }) => (
            <SWhatNowItem key={step.title}>
              <SWhatNowIcon>
                <Icon />
              </SWhatNowIcon>

              <SWhatNowContent>
                <SWhatNowIndex>{step.index}</SWhatNowIndex>
                <SWhatNowTitle>{step.title}</SWhatNowTitle>
                <Spacer size={theme.space.s5} />
                <SWhatNowDescription>{step.description}</SWhatNowDescription>
              </SWhatNowContent>
            </SWhatNowItem>
          ))}
        </SWhatNowGrid>
      </section>

      <Spacer size={theme.space.s10} />

      <section ref={ref} id="delta-link" style={{ padding: "20px" }}>
        <Flex wrap="wrap" justify="center" css={{ gap: theme.space.s6 }}>
          <SManufacturerCard href={WEB_LINKS.deltaLinkGoodwe} target="_blank" rel="noreferrer">
            <SManufacturerCardHeader>
              <Goodwe width={200} height={56} />
            </SManufacturerCardHeader>

            <SManufacturerCardDescription>
              <SDescription>{t("acquisitionForm:goodwe_description")}</SDescription>
            </SManufacturerCardDescription>

            <SManufacturerCardFooter>{t("acquisitionForm:buy_delta_link")}</SManufacturerCardFooter>
          </SManufacturerCard>

          <SManufacturerCard href={WEB_LINKS.deltaLinkSolax} target="_blank" rel="noreferrer">
            <SManufacturerCardHeader>
              <SolaxActive width={200} height={56} />
            </SManufacturerCardHeader>

            <SManufacturerCardDescription>
              <SDescription>{t("acquisitionForm:solax_description")}</SDescription>
            </SManufacturerCardDescription>

            <SManufacturerCardFooter>{t("acquisitionForm:buy_delta_link")}</SManufacturerCardFooter>
          </SManufacturerCard>

          <SManufacturerCard href={WEB_LINKS.proteusLogin} target="_blank" rel="noreferrer">
            <SManufacturerCardHeader>
              <VictronLogo width={200} height={56} />
            </SManufacturerCardHeader>

            <SManufacturerCardDescription>
              <SDescription>{t("acquisitionForm:victron_description")}</SDescription>
            </SManufacturerCardDescription>

            <SManufacturerCardFooter>{t("acquisitionForm:create_account")}</SManufacturerCardFooter>
          </SManufacturerCard>
        </Flex>
      </section>
    </PageContainer>
  )
}
